import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, Swords, MapPin, Users, User, LogOut, UserCircle, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import Logo from "@/components/Logo";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const TABS = [
  { to: "/home", label: "Home", icon: Home, testid: "nav-item-home" },
  { to: "/play", label: "Play", icon: Swords, testid: "nav-item-play" },
  { to: "/courts", label: "Courts", icon: MapPin, testid: "nav-item-courts" },
  { to: "/social", label: "Social", icon: Users, testid: "nav-item-social" },
  { to: "/profile", label: "Profile", icon: User, testid: "nav-item-profile" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen pv-bg-neutral pb-24 md:pb-0">
      {/* Top bar */}
      <header className="pv-bg-dark text-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Logo light />
          <nav className="hidden md:flex items-center gap-1">
            {TABS.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                data-testid={`desktop-${t.testid}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-bold flex items-center gap-2 transition-all ${
                    isActive ? "pv-bg-primary text-black" : "text-white/80 hover:text-white hover:bg-white/10"
                  }`
                }
              >
                <t.icon size={16} />
                {t.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="avatar-menu-trigger"
                  className="rounded-full outline-none focus:ring-2 focus:ring-lime-400 transition-transform hover:scale-105"
                  title="Account menu"
                >
                  <Avatar className="h-9 w-9 border-2 pv-border-primary">
                    <AvatarImage src={user?.avatar} alt={user?.name} />
                    <AvatarFallback>{user?.name?.[0] || "P"}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52" data-testid="avatar-menu-content">
                <DropdownMenuLabel className="truncate">{user?.name || "Player"}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user?.role === "admin" && (
                  <DropdownMenuItem
                    data-testid="menu-admin-link"
                    onClick={() => navigate("/admin/courts")}
                    className="cursor-pointer"
                  >
                    <ShieldCheck size={16} className="mr-2" />
                    Court Review
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  data-testid="menu-profile-link"
                  onClick={() => navigate("/profile")}
                  className="cursor-pointer"
                >
                  <UserCircle size={16} className="mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onClick={handleLogout}
                  className="cursor-pointer text-red-600 focus:text-red-600"
                >
                  <LogOut size={16} className="mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
        <Outlet />
      </main>

      {/* Bottom tabs (mobile) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 pv-bg-dark text-white z-40 border-t border-white/10" data-testid="bottom-nav">
        <div className="grid grid-cols-5">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={t.testid}
              className={({ isActive }) =>
                `flex flex-col items-center py-3 gap-1 text-xs font-semibold relative ${
                  isActive ? "pv-text-primary" : "text-white/70"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute top-0 h-1 w-10 rounded-b-full pv-bg-primary" />
                  )}
                  <t.icon size={22} />
                  {t.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
